import * as THREE from 'three';
import { BoardScene } from '../render/scene';
import {
  CPU_DIFFICULTIES,
  CPU_DIFFICULTY_LABEL,
  decideObservation,
  decidePlacement,
  type CpuDifficulty,
} from '../game/cpu';
import { Game, GameError, MAX_OBSERVATIONS, otherPlayer } from '../game/game';
import { BLACK_PROBABILITY, stoneTypeDescription, stoneTypeLabel } from '../game/stones';
import type { Phase, Player, StoneType, Winner } from '../game/types';
import { FACES, FACE_INFO, sectionFixedValue, type Face } from './crossSection';
import { faceCubeIcon } from './faceIcon';
import {
  DEFAULT_SETTINGS,
  parseSettings,
  serializeSettings,
  SETTINGS_STORAGE_KEY,
  type GameMode,
  type MatchSettings,
} from './matchSettings';
import { SectionGrid } from './sectionGrid';
import {
  resolveInitialTheme,
  SCENE_THEMES,
  THEME_STORAGE_KEY,
  type ThemeName,
} from './theme';

/** 黒確率 p を「黒→白」の補間色（CSS）にする */
function probabilityColor(p: number): string {
  const lerp = (a: number, b: number) => Math.round(a + (b - a) * p);
  const r = lerp(245, 20);
  const g = lerp(245, 20);
  const b = lerp(245, 20);
  return `rgb(${r}, ${g}, ${b})`;
}

const PHASE_LABEL: Record<Phase, string> = {
  selecting: '石を配置中（種類を選んで盤面をクリック）',
  deciding: '観測するか選択中',
  result: '観測結果を確認中',
  over: 'ゲーム終了',
};

const PLAYER_LABEL: Record<Player, string> = {
  first: '先手（黒）',
  second: '後手（白）',
};

/** 断面操作の状態（表示専用。ゲーム状態とは独立） */
interface SectionState {
  face: Face;
  depth: number;
}

const COORD_HINT = 'マスにカーソルを合わせると 3D 座標を表示';

/** 保存されたテーマを読み取る（利用不可なら null） */
function readStoredTheme(): string | null {
  try {
    return window.localStorage.getItem(THEME_STORAGE_KEY);
  } catch {
    return null;
  }
}

/** システムの配色設定（prefers-color-scheme: light か） */
function prefersSystemLight(): boolean {
  try {
    return window.matchMedia('(prefers-color-scheme: light)').matches;
  } catch {
    return false;
  }
}

/** 保存された対戦設定を読み取る（利用不可・不正ならデフォルト） */
function readStoredSettings(): MatchSettings {
  try {
    return parseSettings(window.localStorage.getItem(SETTINGS_STORAGE_KEY));
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

/** 対戦設定を localStorage に保存する（失敗しても動作は継続する） */
function saveStoredSettings(settings: MatchSettings): void {
  try {
    window.localStorage.setItem(SETTINGS_STORAGE_KEY, serializeSettings(settings));
  } catch {
    // 保存できない環境（プライベートモード等）では記憶しないだけで動作継続
  }
}

/**
 * DOM 上の UI（状態表示・石の選択・操作ボタン・断面グリッド）と 3D シーン、
 * ゲームロジックを繋ぐコントローラ。
 *
 * 断面（切り出し）操作は表示の切り替えのみを行い、
 * 石の配置は常に既存の game.placeStone 経由で行う。
 */
export class App {
  private readonly game: Game;
  private readonly scene: BoardScene;
  private readonly root: HTMLElement;
  private readonly sectionGrid: SectionGrid;

  private readonly panel: HTMLElement;
  private readonly viewport: HTMLElement;
  private readonly sectionPanel: HTMLElement;
  private phaseEl!: HTMLElement;
  private turnEl!: HTMLElement;
  private obsEl!: HTMLElement;
  private winnerEl!: HTMLElement;
  private cpuStatusEl!: HTMLElement;
  private stoneButtonsEl!: HTMLElement;
  private actionsEl!: HTMLElement;
  private messageEl!: HTMLElement;
  private themeButton!: HTMLButtonElement;
  private modeSelect!: HTMLSelectElement;
  private sideSelect!: HTMLSelectElement;
  private difficultySelect!: HTMLSelectElement;

  private faceButtonsEl!: HTMLElement;
  private depthInput!: HTMLInputElement;
  private depthValueEl!: HTMLElement;
  private sectionBadge!: HTMLElement;
  private statusFaceEl!: HTMLElement;
  private statusDepthEl!: HTMLElement;
  private statusSectionEl!: HTMLElement;
  private gridContainer!: HTMLElement;
  private gridEmptyEl!: HTMLElement;
  private coordEl!: HTMLElement;
  private aimSectionButton!: HTMLButtonElement;

  private settings: MatchSettings;
  /** CPU 自動着手の世代番号。リセット・設定変更で増やし、古いタイマーステップを無効化 */
  private cpuToken = 0;
  /** CPU の着手が進行中かどうか（多重起動防止） */
  private cpuBusy = false;

  private selectedType: StoneType | null = null;
  private section: SectionState | null = null;
  private theme: ThemeName;

  constructor(root: HTMLElement) {
    this.root = root;
    this.game = new Game();
    this.theme = resolveInitialTheme(readStoredTheme(), prefersSystemLight());
    this.settings = readStoredSettings();

    this.root.classList.add('app');
    this.panel = this.el('aside', 'panel');
    this.viewport = this.el('main', 'viewport');
    this.sectionPanel = this.el('aside', 'section-panel');
    this.root.append(this.panel, this.viewport, this.sectionPanel);

    this.buildPanel();
    this.buildSectionPanel();

    this.scene = new BoardScene(this.viewport, this.theme);
    this.scene.onCellPick = (x, y, z) => this.handleCellPick(x, y, z);
    // 3D の断面面ドラッグもスライダーと同じ深さ設定経路を通す（単一の状態を維持）
    this.scene.onSectionDepthChange = (depth) => this.setSectionDepth(depth);
    this.applyTheme(this.theme);

    this.sectionGrid = new SectionGrid(this.gridContainer);
    this.sectionGrid.onCellClick = (x, y, z) => this.handleCellPick(x, y, z);
    this.sectionGrid.onCellHover = (x, y, z) => {
      this.coordEl.textContent = `選択セル: x = ${x}, y = ${y}, z = ${z}`;
    };
    this.sectionGrid.onGridLeave = () => {
      this.coordEl.textContent = COORD_HINT;
    };

    this.updateSectionStatus();
    this.refresh();
  }

  // ---------- DOM ビルド ----------

  private el<K extends keyof HTMLElementTagNameMap>(
    tag: K,
    className?: string,
  ): HTMLElementTagNameMap[K] {
    const node = document.createElement(tag);
    if (className) node.className = className;
    return node;
  }

  private buildPanel(): void {
    const title = this.el('h1', 'title');
    title.textContent = '3D量子五目並べ';
    this.panel.append(title);

    // 対戦設定（モード・自分の手番・CPU 難易度）。選択は localStorage に記憶される
    const settings = this.el('section', 'section');
    const settingsTitle = this.el('h2', 'section-title');
    settingsTitle.textContent = '対戦設定';
    this.modeSelect = this.settingSelect(settings, '対戦モード', [
      ['pvp', '2人対戦'],
      ['cpu', 'CPU対戦'],
    ]);
    this.modeSelect.addEventListener('change', () => this.onModeChange());
    this.sideSelect = this.settingSelect(settings, '自分の手番', [
      ['first', '先手（黒）'],
      ['second', '後手（白）'],
    ]);
    this.sideSelect.addEventListener('change', () => this.onSideChange());
    this.difficultySelect = this.settingSelect(
      settings,
      'CPU 難易度',
      CPU_DIFFICULTIES.map((difficulty) => [difficulty, CPU_DIFFICULTY_LABEL[difficulty]]),
    );
    this.difficultySelect.addEventListener('change', () => this.onDifficultyChange());
    this.panel.append(settings);
    this.syncSettingsUi();

    // テーマ切替（ダーク/ライト）。選択は localStorage に記憶される
    const display = this.el('section', 'section');
    const displayTitle = this.el('h2', 'section-title');
    displayTitle.textContent = '表示';
    this.themeButton = this.el('button', 'action-btn ghost theme-btn');
    this.themeButton.addEventListener('click', () => this.toggleTheme());
    display.append(displayTitle, this.themeButton);
    this.panel.append(display);

    const status = this.el('section', 'status');
    this.phaseEl = this.el('div', 'status-row phase');
    this.turnEl = this.el('div', 'status-row');
    this.obsEl = this.el('div', 'status-row');
    this.winnerEl = this.el('div', 'status-row winner');
    this.cpuStatusEl = this.el('div', 'status-row cpu-status');
    this.cpuStatusEl.hidden = true;
    status.append(this.phaseEl, this.turnEl, this.obsEl, this.winnerEl, this.cpuStatusEl);
    this.panel.append(status);

    const stones = this.el('section', 'section');
    const stonesTitle = this.el('h2', 'section-title');
    stonesTitle.textContent = '石の種類';
    this.stoneButtonsEl = this.el('div', 'stone-buttons');
    stones.append(stonesTitle, this.stoneButtonsEl);
    this.panel.append(stones);

    this.actionsEl = this.el('section', 'actions');
    this.panel.append(this.actionsEl);

    this.messageEl = this.el('section', 'message');
    this.panel.append(this.messageEl);

    const help = this.el('section', 'section help');
    const helpTitle = this.el('h2', 'section-title');
    helpTitle.textContent = '遊び方';
    const ul = this.el('ul');
    for (const text of [
      '「対戦設定」で CPU 対戦を選べば、自分の手番と難易度を指定して CPU と対戦できる。',
      '先手は黒（90/70/50%）、後手は白（30/10/50%）の石を置く。',
      '盤面をクリックしてマスを選び、石を配置する。',
      '内部のマスは右の「断面操作」で面と深さを選び、2D グリッドから置ける。',
      '断面の面・深さを選ぶと視点は自動で動かない。「視点を断面へ移動」で調整できる。',
      '3D の断面面をドラッグすると深さを変更できます（スライダーと同期）。クリックはそのまま石の配置になります。',
      '置いた後、観測するか選択できる（各プレイヤー 5 回まで）。',
      '観測すると全石の色が決まり、5 列以上で勝ち。',
      'ドラッグで回転、右ドラッグで移動、ホイールでズーム。',
    ]) {
      const li = this.el('li');
      li.textContent = text;
      ul.append(li);
    }
    help.append(helpTitle, ul);
    this.panel.append(help);
  }

  /** ラベル付きの <select> を生成して parent に追加する */
  private settingSelect(
    parent: HTMLElement,
    label: string,
    options: ReadonlyArray<readonly [string, string]>,
  ): HTMLSelectElement {
    const wrap = this.el('label', 'setting');
    const name = this.el('span', 'setting-label');
    name.textContent = label;
    const select = this.el('select', 'setting-select');
    for (const [value, text] of options) {
      const option = this.el('option');
      option.value = value;
      option.textContent = text;
      select.append(option);
    }
    wrap.append(name, select);
    parent.append(wrap);
    return select;
  }

  // ---------- 対戦設定 ----------

  /** 設定 UI を現在の settings に同期する（モードに依存する項目の有効/無効も） */
  private syncSettingsUi(): void {
    this.modeSelect.value = this.settings.mode;
    this.sideSelect.value = this.settings.playerSide;
    this.difficultySelect.value = this.settings.difficulty;
    const isCpu = this.settings.mode === 'cpu';
    this.sideSelect.disabled = !isCpu;
    this.difficultySelect.disabled = !isCpu;
  }

  private applySettings(next: MatchSettings): void {
    this.settings = next;
    saveStoredSettings(next);
    this.syncSettingsUi();
  }

  /** モード変更: 対戦形式が変わるため新しいゲームを開始する */
  private onModeChange(): void {
    const mode = (this.modeSelect.value === 'cpu' ? 'cpu' : 'pvp') as GameMode;
    if (mode === this.settings.mode) return;
    this.applySettings({ ...this.settings, mode });
    this.newGame();
    this.showMessage(
      mode === 'cpu'
        ? `CPU 対戦を開始しました。あなたは${PLAYER_LABEL[this.settings.playerSide]}です。`
        : '2人対戦を開始しました。',
      'info',
    );
  }

  /** 自分の手番変更: 先手/後手が入れ替わるため新しいゲームを開始する */
  private onSideChange(): void {
    const playerSide = (this.sideSelect.value === 'second' ? 'second' : 'first') as Player;
    if (playerSide === this.settings.playerSide) return;
    this.applySettings({ ...this.settings, playerSide });
    this.newGame();
    this.showMessage(
      `あなたは${PLAYER_LABEL[playerSide]}として対戦します（新しいゲームを開始）。`,
      'info',
    );
  }

  /** 難易度変更: 次局から反映。進行中のゲームは継続する */
  private onDifficultyChange(): void {
    const raw = this.difficultySelect.value;
    if (!(CPU_DIFFICULTIES as readonly string[]).includes(raw)) return;
    const difficulty = raw as CpuDifficulty;
    if (difficulty === this.settings.difficulty) return;
    this.applySettings({ ...this.settings, difficulty });
    this.showMessage(
      `CPU 難易度を「${CPU_DIFFICULTY_LABEL[difficulty]}」に変更しました（次局から反映）。`,
      'info',
    );
  }

  private buildSectionPanel(): void {
    const title = this.el('h2', 'title section-title-inline');
    title.textContent = '断面操作';
    this.sectionPanel.append(title);

    this.sectionBadge = this.el('div', 'section-badge');
    this.sectionBadge.textContent = '断面モード中 — 表示のみの切り出し（ゲーム状態は不変）';
    this.sectionBadge.hidden = true;
    this.sectionPanel.append(this.sectionBadge);

    // 6 面の選択（立方体図付き）
    const faceBlock = this.el('section', 'section-block');
    this.faceButtonsEl = this.el('div', 'face-buttons');
    for (const face of FACES) {
      const button = this.el('button', 'face-btn');
      const icon = this.el('span', 'face-icon');
      icon.innerHTML = faceCubeIcon(face);
      const label = this.el('span', 'face-label');
      label.textContent = FACE_INFO[face].label;
      button.append(icon, label);
      button.addEventListener('click', () => this.selectFace(face));
      this.faceButtonsEl.append(button);
    }
    faceBlock.append(this.faceButtonsEl);
    this.sectionPanel.append(faceBlock);

    // 深さ 0〜9
    const depthBlock = this.el('section', 'section-block');
    const depthRow = this.el('div', 'depth-row');
    const depthLabel = this.el('label');
    depthLabel.append('深さ ');
    this.depthValueEl = this.el('span', 'depth-value');
    this.depthValueEl.textContent = '0';
    depthLabel.append(this.depthValueEl);
    this.depthInput = this.el('input');
    this.depthInput.type = 'range';
    this.depthInput.min = '0';
    this.depthInput.max = '9';
    this.depthInput.step = '1';
    this.depthInput.value = '0';
    this.depthInput.addEventListener('input', () => this.setSectionDepth(Number(this.depthInput.value)));
    depthRow.append(depthLabel, this.depthInput);
    depthBlock.append(depthRow);
    this.sectionPanel.append(depthBlock);

    // 視点を断面へ手動で移動する（面・深さの選択時は視点を自動で動かさない）
    const viewBlock = this.el('section', 'section-block');
    this.aimSectionButton = this.el('button', 'action-btn ghost section-aim');
    this.aimSectionButton.textContent = '視点を断面へ移動';
    this.aimSectionButton.disabled = true;
    this.aimSectionButton.addEventListener('click', () => this.aimSection());
    viewBlock.append(this.aimSectionButton);
    this.sectionPanel.append(viewBlock);

    // 現在の面・深さ・断面座標
    const status = this.el('div', 'section-status');
    this.statusFaceEl = this.el('div');
    this.statusDepthEl = this.el('div');
    this.statusSectionEl = this.el('div');
    status.append(this.statusFaceEl, this.statusDepthEl, this.statusSectionEl);
    this.sectionPanel.append(status);

    // 10×10 の断面グリッド
    const gridWrap = this.el('div', 'section-grid-wrap');
    this.gridEmptyEl = this.el('div', 'muted grid-empty');
    this.gridEmptyEl.textContent = '面を選択すると断面グリッドが表示されます';
    this.gridContainer = this.el('div');
    gridWrap.append(this.gridEmptyEl, this.gridContainer);
    this.sectionPanel.append(gridWrap);

    this.coordEl = this.el('div', 'section-coord muted');
    this.coordEl.textContent = COORD_HINT;
    this.sectionPanel.append(this.coordEl);

    const clear = this.el('button', 'action-btn ghost section-clear');
    clear.textContent = '全体表示に戻る';
    clear.addEventListener('click', () => this.clearSection());
    this.sectionPanel.append(clear);
  }

  // ---------- 断面操作 ----------

  private selectFace(face: Face): void {
    // スライダーの現在の深さを適用する（面未選択で先に深さを決めた場合にも反映）
    // 注: 視点は自動で移動しない（ユーザーの回転・ズームを維持する）
    this.section = { face, depth: Number(this.depthInput.value) };
    this.renderFaceButtons();
    this.depthValueEl.textContent = String(this.section.depth);
    this.applySection();
  }

  /**
   * 断面の深さを変更する（スライダーと 3D 面のドラッグの共通入口）。
   * スライダーの値と section.depth を同時に更新するので、
   * どちらから操作しても両者は常に同じ深さを指す。
   */
  private setSectionDepth(rawDepth: number): void {
    const depth = Math.min(9, Math.max(0, Math.round(rawDepth)));
    this.depthInput.value = String(depth);
    this.depthValueEl.textContent = String(depth);
    if (this.section === null) {
      // 面がまだ選ばれる前に深さだけを動かした場合はプレビュー表示のみ
      this.statusFaceEl.textContent = '切り出し面:（面を選んでください）';
      this.statusDepthEl.textContent = `深さ: ${depth}`;
      this.statusSectionEl.textContent = '断面座標: —';
      return;
    }
    this.section.depth = depth;
    this.applySection();
  }

  private clearSection(): void {
    this.section = null;
    this.renderFaceButtons();
    this.depthInput.value = '0';
    this.depthValueEl.textContent = '0';
    this.applySection();
  }

  /** 視点を現在の断面へ手動で移動する（全体表示中は無操作） */
  private aimSection(): void {
    if (this.section === null) return;
    this.scene.aimAtSection();
  }

  private renderFaceButtons(): void {
    const buttons = this.faceButtonsEl.children;
    FACES.forEach((face, i) => {
      buttons[i].classList.toggle('selected', this.section?.face === face);
    });
  }

  /** 断面状態を 3D 表示と 2D グリッドに反映する（ゲーム状態・視点には変更しない） */
  private applySection(): void {
    this.aimSectionButton.disabled = this.section === null;
    if (this.section === null) {
      this.scene.clearCutaway();
      this.sectionGrid.hide();
      this.gridEmptyEl.hidden = false;
      this.sectionBadge.hidden = true;
      this.coordEl.textContent = COORD_HINT;
      this.updateSectionStatus();
      return;
    }
    this.gridEmptyEl.hidden = true;
    this.sectionBadge.hidden = false;
    this.scene.setCutaway(this.section.face, this.section.depth);
    this.renderSectionGrid();
    this.updateSectionStatus();
  }

  /** ゲーム状態から断面グリッドを再描画する（石の増減・フェーズに応じる） */
  private renderSectionGrid(): void {
    if (this.section === null) return;
    this.sectionGrid.render(
      this.section.face,
      this.section.depth,
      this.game,
      this.game.phase === 'selecting' && !this.isCpuTurn(),
    );
  }

  private updateSectionStatus(): void {
    if (this.section === null) {
      this.statusFaceEl.textContent = '切り出し面: 全体表示';
      this.statusDepthEl.textContent = '深さ: —';
      this.statusSectionEl.textContent = '断面座標: —';
      return;
    }
    const { face, depth } = this.section;
    const info = FACE_INFO[face];
    const v = sectionFixedValue(face, depth);
    this.statusFaceEl.textContent = `切り出し面: ${info.label}`;
    this.statusDepthEl.textContent = `深さ: ${depth}`;
    this.statusSectionEl.textContent = `断面座標: ${info.fixed} = ${v}`;
  }

  // ---------- テーマ ----------

  /** テーマを CSS（:root の data-theme）、3D シーン、localStorage に適用する */
  private applyTheme(theme: ThemeName): void {
    this.theme = theme;
    document.documentElement.dataset.theme = theme;
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch {
      // 保存できない環境（プライベートモード等）では記憶しないだけで動作継続
    }
    this.scene.setTheme(theme);
    this.themeButton.textContent = theme === 'dark' ? 'ライトテーマにする' : 'ダークテーマにする';
    this.themeButton.setAttribute('aria-pressed', String(theme === 'light'));
  }

  private toggleTheme(): void {
    this.applyTheme(this.theme === 'dark' ? 'light' : 'dark');
    // ホバーハイライトの色はテーマに依存するため再適用する
    this.refresh();
  }

  // ---------- CPU 対戦 ----------

  /** CPU が対戦相手である側の player（2人対戦では null） */
  private get cpuSide(): Player | null {
    return this.settings.mode === 'cpu' ? otherPlayer(this.settings.playerSide) : null;
  }

  /** 現在が CPU の手番かどうか（対局終了後は false） */
  private isCpuTurn(): boolean {
    const side = this.cpuSide;
    return side !== null && this.game.phase !== 'over' && this.game.currentPlayer === side;
  }

  /**
   * 手番表示（CPU 対戦時は「あなた / CPU」を付加する）
   */
  private playerLabel(player: Player): string {
    const cpu = this.cpuSide;
    if (cpu === null) return PLAYER_LABEL[player];
    const role = player === this.settings.playerSide ? 'あなた' : 'CPU';
    return `${PLAYER_LABEL[player]}（${role}）`;
  }

  private winnerText(winner: Winner): string {
    if (winner === null) return '進行中';
    if (winner === 'draw') return '引き分け';
    return `${this.playerLabel(winner)}の勝利`;
  }

  /** CPU の手番中なら、短い思考遅延の後に 1 手を進める（ユーザーに経過が見えるようにするため） */
  private maybeCpuAct(): void {
    if (this.cpuBusy || !this.isCpuTurn()) return;
    const phase = this.game.phase;
    if (phase !== 'selecting' && phase !== 'deciding' && phase !== 'result') return;
    this.cpuBusy = true;
    const token = this.cpuToken;
    this.showMessage('CPUが考えています…', 'info');
    window.setTimeout(() => {
      // リセット・設定変更で世代が変わっていれば、古いステップは無視する
      if (token !== this.cpuToken) return;
      if (!this.isCpuTurn()) {
        this.cpuBusy = false;
        return;
      }
      try {
        this.actCpuOnce();
        // 1 動作完了。まだ CPU の手番なら（例: 配置後の観測判断）、
        // refresh 内の maybeCpuAct が次のステップを予定する
        this.cpuBusy = false;
        this.refresh();
      } catch (error) {
        // エラーで自動着手を停止する（無限リトライを防ぐ）。ユーザーはリセットで再開できる
        this.cpuToken++;
        this.cpuBusy = false;
        this.showMessage(
          error instanceof GameError ? error.message : 'CPU の着手でエラーが発生しました。',
          'error',
        );
      }
    }, phase === 'result' ? 1000 : 700);
  }

  /** CPU が人間と同じ Game API を経由で 1 動作（配置 / 観測判断 / 結果確認）を行う */
  private actCpuOnce(): void {
    const player = this.game.currentPlayer;
    const difficulty = this.settings.difficulty;
    const label = CPU_DIFFICULTY_LABEL[difficulty];
    if (this.game.phase === 'selecting') {
      const move = decidePlacement(this.game.state, difficulty, Math.random);
      this.game.placeStone(move.x, move.y, move.z, move.type);
      const remaining = MAX_OBSERVATIONS - this.game.state.players[player].observationsUsed;
      this.showMessage(
        remaining > 0
          ? `CPU（${label}）が ${move.type}% の石を (x=${move.x}, y=${move.y}, z=${move.z}) に配置しました。`
          : `CPU（${label}）が ${move.type}% の石を (x=${move.x}, y=${move.y}, z=${move.z}) に配置しました（観測権利なし）。`,
        'info',
      );
    } else if (this.game.phase === 'deciding') {
      if (decideObservation(this.game.state, difficulty, Math.random)) {
        this.game.observe();
        this.showMessage(`CPU（${label}）が観測しました（全石の色が確定）。`, 'info');
      } else {
        this.game.skipObservation();
        this.showMessage(`CPU（${label}）は観測しませんでした（パス）。`, 'info');
      }
    } else {
      // phase === 'result'
      this.game.confirmResult();
      this.showMessage('CPU が観測結果を確認しました（勝者なし、石の色はリセット）。', 'info');
    }
  }

  /** ステータス領域に CPU の手番・進行状況を表示する */
  private renderCpuStatus(): void {
    if (!this.isCpuTurn()) {
      this.cpuStatusEl.hidden = true;
      this.cpuStatusEl.textContent = '';
      return;
    }
    const phase = this.game.phase;
    const stage =
      phase === 'selecting'
        ? '石の種類と場所を選んで配置中'
        : phase === 'deciding'
          ? '観測するかどうか判断中'
          : phase === 'result'
            ? '観測結果を確認中'
            : '';
    this.cpuStatusEl.textContent = `CPU の手番（${CPU_DIFFICULTY_LABEL[this.settings.difficulty]}）— ${stage}`;
    this.cpuStatusEl.hidden = false;
  }

  // ---------- 更新 ----------

  private refresh(): void {
    const { phase, currentPlayer, winner } = this.game;
    const obs = this.game.getObservationCounts();

    this.phaseEl.textContent = PHASE_LABEL[phase];
    this.turnEl.textContent = `手番: ${this.playerLabel(currentPlayer)}`;
    this.obsEl.textContent = `観測: 先手 ${obs.first}/${MAX_OBSERVATIONS} ・ 後手 ${obs.second}/${MAX_OBSERVATIONS}`;
    this.winnerEl.textContent = `勝者: ${this.winnerText(winner)}`;
    this.winnerEl.classList.toggle('final', phase === 'over');
    this.renderCpuStatus();

    this.renderStoneButtons();
    this.renderActions();

    // CPU 対戦では CPU の手番中は盤面への入力を無効にする
    this.scene.setInteractionEnabled(phase === 'selecting' && !this.isCpuTurn());
    // 現在のプレイヤーの目標色に寄せたハイライト色（テーマごとに背景へ馴染む色を使う）
    const palette = SCENE_THEMES[this.theme];
    this.scene.setHoverColor(
      new THREE.Color(currentPlayer === 'first' ? palette.hoverFirst : palette.hoverSecond),
    );
    this.scene.sync(this.game);
    this.renderSectionGrid();

    this.maybeCpuAct();
  }

  private renderStoneButtons(): void {
    this.stoneButtonsEl.textContent = '';
    const selectable = this.game.getSelectableStones();

    // 現在の選択がもう使えなければ、先頭を自動選択する
    if (this.selectedType === null || !selectable.includes(this.selectedType)) {
      this.selectedType = selectable[0] ?? null;
    }

    for (const type of selectable) {
      const button = this.el('button', 'stone-btn');
      if (type === this.selectedType) button.classList.add('selected');
      button.title = stoneTypeDescription(type);

      const swatch = this.el('span', 'swatch');
      swatch.style.background = probabilityColor(BLACK_PROBABILITY[type]);
      const label = this.el('span', 'stone-label');
      label.textContent = stoneTypeLabel(type);

      button.append(swatch, label);
      button.disabled = this.isCpuTurn();
      button.addEventListener('click', () => {
        if (this.isCpuTurn()) return;
        this.selectedType = type;
        this.renderStoneButtons();
      });
      this.stoneButtonsEl.append(button);
    }

    if (selectable.length === 0) {
      const empty = this.el('span', 'muted');
      empty.textContent = '使用できる石がありません';
      this.stoneButtonsEl.append(empty);
    }
  }

  private renderActions(): void {
    this.actionsEl.textContent = '';
    const { phase } = this.game;
    // CPU の手番中は観測判断・結果確認のボタンを隠す（CPU が自動で行う）
    const cpuActing = this.isCpuTurn();

    if (phase === 'deciding' && !cpuActing) {
      this.actionsEl.append(
        this.actionButton('観測する', 'primary', () => {
          this.game.observe();
          this.showMessage('観測しました。', 'info');
          this.refresh();
        }),
        this.actionButton('スキップ', 'ghost', () => {
          this.game.skipObservation();
          this.refresh();
        }),
      );
    } else if (phase === 'result' && !cpuActing) {
      this.actionsEl.append(
        this.actionButton('結果を確認', 'primary', () => {
          this.game.confirmResult();
          this.refresh();
        }),
      );
    }

    if (phase === 'over') {
      this.actionsEl.append(
        this.actionButton('新しいゲーム', 'primary', () => this.newGame()),
      );
    } else {
      this.actionsEl.append(
        this.actionButton('リセット', 'ghost', () => this.newGame()),
      );
    }
  }

  private actionButton(label: string, kind: string, onClick: () => void): HTMLButtonElement {
    const button = this.el('button', `action-btn ${kind}`);
    button.textContent = label;
    button.addEventListener('click', onClick);
    return button;
  }

  // ---------- イベント ----------

  private handleCellPick(x: number, y: number, z: number): void {
    if (this.game.phase !== 'selecting') return;
    if (this.isCpuTurn()) return;
    if (this.selectedType === null) {
      this.showMessage('先に石の種類を選んでください。', 'error');
      return;
    }
    try {
      this.game.placeStone(x, y, z, this.selectedType);
      this.messageEl.textContent = '';
      this.messageEl.className = 'message';
      this.refresh();
    } catch (error) {
      this.showMessage(error instanceof GameError ? error.message : '配置できませんでした。', 'error');
    }
  }

  private newGame(): void {
    // 進行中の CPU 自動着手（タイマー）を無効化する
    this.cpuToken++;
    this.cpuBusy = false;
    this.game.restart();
    this.selectedType = null;
    this.messageEl.textContent = '';
    this.messageEl.className = 'message';
    // 断面表示も初期状態（全体表示）に戻す（視点は変えない）
    this.section = null;
    this.renderFaceButtons();
    this.depthInput.value = '0';
    this.depthValueEl.textContent = '0';
    this.applySection();
    this.refresh();
  }

  private showMessage(text: string, kind: 'error' | 'info'): void {
    this.messageEl.textContent = text;
    this.messageEl.className = `message ${kind}`;
  }
}
